import { useMap } from '@vis.gl/react-google-maps'
import { useLayoutEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { getListingHoverPopoverPlacement } from '../listings/listingHoverPopoverPosition'

type GoogleMapContainedPopoverProps = {
  children: ReactNode
  className?: string
  defaultSize: { height: number; width: number }
  pixel: { x: number; y: number }
}

export function GoogleMapContainedPopover({
  children,
  className,
  defaultSize,
  pixel,
}: GoogleMapContainedPopoverProps) {
  const map = useMap()
  const [element, setElement] = useState<HTMLDivElement | null>(null)
  const [popoverSize, setPopoverSize] = useState(defaultSize)
  const [mapSize, setMapSize] = useState({ height: 0, width: 0 })

  useLayoutEffect(() => {
    if (!map || !element) return

    const mapElement = map.getDiv()
    const updateSizes = () => {
      setMapSize({
        height: mapElement.clientHeight,
        width: mapElement.clientWidth,
      })
      setPopoverSize({
        height: element.offsetHeight,
        width: element.offsetWidth,
      })
    }
    const observer = new ResizeObserver(updateSizes)

    observer.observe(mapElement)
    observer.observe(element)
    updateSizes()

    return () => observer.disconnect()
  }, [element, map])

  if (!map) return null

  const mapElement = map.getDiv()
  const resolvedMapSize =
    mapSize.height > 0 && mapSize.width > 0
      ? mapSize
      : {
          height: mapElement.clientHeight,
          width: mapElement.clientWidth,
        }

  if (resolvedMapSize.height === 0 || resolvedMapSize.width === 0) return null

  const placement = getListingHoverPopoverPlacement({
    anchor: pixel,
    mapSize: resolvedMapSize,
    popoverSize,
  })
  const arrowLeft = Math.min(
    Math.max(pixel.x - placement.left, 16),
    popoverSize.width - 16
  )

  return createPortal(
    <div
      ref={setElement}
      className={className}
      style={{ left: placement.left, top: placement.top }}>
      <div className="relative z-20">{children}</div>
      <div
        className={
          placement.side === 'above'
            ? 'absolute z-10 top-full h-4 w-4 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-background'
            : 'absolute bottom-full z-10 h-4 w-4 -translate-x-1/2 translate-y-1/2 rotate-45 bg-background'
        }
        style={{ left: arrowLeft }}
      />
    </div>,
    mapElement
  )
}
